import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Edit, LogOut, UserX, UserCheck, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { EmployeeForm } from '@/components/hr/employee/employee-form'
import { EmployeeProfile } from '@/components/hr/employee/employee-profile'
import { OffboardingWizard } from '@/components/hr/employee/offboarding-wizard'
import { useConfirmDialog } from '@/hooks'
import {
  useEmployee,
  useDisableEmployee,
  useEnableEmployee,
  useDeleteEmployee,
  type EmployeeStatus,
  type CompanyRole,
} from '@/hooks'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/hr/employees/$employeeId')({
  component: EmployeeDetailPage,
})

const STATUS_LABELS: Record<EmployeeStatus, string> = {
  active: '在职',
  on_leave: '休假中',
  resigned: '已离职',
}

const STATUS_COLORS: Record<EmployeeStatus, string> = {
  active: 'bg-green-100 text-green-800',
  on_leave: 'bg-yellow-100 text-yellow-800',
  resigned: 'bg-gray-100 text-gray-800',
}

const ROLE_LABELS: Record<CompanyRole, string> = {
  director: '主管',
  finance: '财务',
  hr: '人事',
  admin: '行政',
  ops: '运维',
  pm: '项目经理',
  developer: '开发人员',
}

function EmployeeDetailPage() {
  const params = Route.useParams()
  const employeeId = params.employeeId
  const navigate = useNavigate()
  const [showEditForm, setShowEditForm] = useState(false)
  const [showOffboarding, setShowOffboarding] = useState(false)
  const [showProfile, setShowProfile] = useState(true)

  const { data: employee, isLoading, error } = useEmployee(employeeId)
  const disableMutation = useDisableEmployee()
  const enableMutation = useEnableEmployee()
  const deleteMutation = useDeleteEmployee()
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

  const handleDelete = async () => {
    const confirmed = await confirmWithPreset(
      'delete',
      '确定要删除该员工吗？此操作不可恢复。'
    )
    if (confirmed) {
      setLoading(true)
      try {
        await deleteMutation.mutateAsync(employeeId)
        void navigate({ to: '/hr/employees' })
      } finally {
        setLoading(false)
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">加载中...</div>
      </div>
    )
  }

  if (error || !employee) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="text-destructive">{error?.message ?? '员工不存在'}</div>
        <Link to={'/hr/employees'} className="text-primary hover:underline">
          返回员工列表
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 返回和操作 */}
      <div className="flex items-center justify-between">
        <Link to={'/hr/employees'} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
          返回员工列表
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowEditForm(true)}
            className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
          >
            <Edit className="h-4 w-4" />
            编辑
          </button>
          {employee.status === 'active' && (
            <>
              <button
                onClick={() => disableMutation.mutate(employeeId)}
                disabled={disableMutation.isPending}
                className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm text-orange-600 hover:bg-orange-50"
              >
                <UserX className="h-4 w-4" />
                禁用账号
              </button>
              <button
                onClick={() => setShowOffboarding(true)}
                className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10"
              >
                <LogOut className="h-4 w-4" />
                开始离职
              </button>
            </>
          )}
          {employee.status === 'resigned' && (
            <button
              onClick={() => enableMutation.mutate(employeeId)}
              disabled={enableMutation.isPending}
              className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm text-green-600 hover:bg-green-50"
            >
              <UserCheck className="h-4 w-4" />
              重新激活
            </button>
          )}
          <button
            onClick={() => void handleDelete()}
            disabled={deleteMutation.isPending}
            className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
            删除
          </button>
        </div>
      </div>

      {/* 编辑表单 */}
      {showEditForm && (
        <EmployeeForm employee={employee} onClose={() => setShowEditForm(false)} onSuccess={() => setShowEditForm(false)} />
      )}

      {/* 离职向导 */}
      {showOffboarding && (
        <OffboardingWizard
          employee={employee}
          onClose={() => setShowOffboarding(false)}
          onSuccess={() => {
            setShowOffboarding(false)
            void navigate({ to: '/hr/employees' })
          }}
        />
      )}

      {/* 视图切换 */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setShowProfile(true)}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px',
            showProfile ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          全景视图
        </button>
        <button
          onClick={() => setShowProfile(false)}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px',
            !showProfile ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          基本信息
        </button>
      </div>

      {/* 内容区域 */}
      {showProfile ? (
        <EmployeeProfile employeeId={employeeId} />
      ) : (
        /* 员工信息卡片 - 原有的基本信息视图 */
        <div className="rounded-lg border bg-card">
          <div className="border-b px-6 py-4">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-2xl font-bold text-primary">
                {employee.name.charAt(0)}
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold">{employee.name}</h1>
                  <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-medium', STATUS_COLORS[employee.status])}>
                    {STATUS_LABELS[employee.status]}
                  </span>
                </div>
                <p className="text-muted-foreground">
                  {employee.employeeNo} · {ROLE_LABELS[employee.role]}
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-6 p-6 md:grid-cols-2">
            {/* 基本信息 */}
            <div className="space-y-4">
              <h3 className="font-semibold">基本信息</h3>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">邮箱</dt>
                  <dd>{employee.email}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">电话</dt>
                  <dd>{employee.phone || '-'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">入职日期</dt>
                  <dd>{new Date(employee.hireDate).toLocaleDateString('zh-CN')}</dd>
                </div>
                {employee.resignDate && (
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">离职日期</dt>
                    <dd>{new Date(employee.resignDate).toLocaleDateString('zh-CN')}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">最后登录</dt>
                  <dd>{employee.lastLoginAt ? new Date(employee.lastLoginAt).toLocaleString('zh-CN') : '-'}</dd>
                </div>
              </dl>
            </div>

            {/* 组织信息 */}
            <div className="space-y-4">
              <h3 className="font-semibold">组织信息</h3>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">部门</dt>
                  <dd>{employee.department?.name || '-'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">职位</dt>
                  <dd>{employee.position?.name || '-'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">直属上级</dt>
                  <dd>{employee.manager?.name || '-'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">角色</dt>
                  <dd>{ROLE_LABELS[employee.role]}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog />
    </div>
  )
}
